import * as ProgressPrimitive from '@radix-ui/react-progress'
import { type ReactNode, useId } from 'react'

import { cn } from '#app/utils/misc.tsx'

export type ProgressProps = {
	/** Completion, 0–100. Values outside the range are clamped; non-finite → 0. */
	value: number
	/** Track thickness. @default 'md' */
	size?: 'md' | 'sm'
	/**
	 * Optional label rendered in a row above the track, paired with the mono
	 * percentage on the right. Omit for a bare track.
	 */
	label?: ReactNode
	/**
	 * Accessible name for the `role="progressbar"`. A labelled bar is named by
	 * its visible `label`; a bare track needs this, else it fails axe
	 * `aria-progressbar-name`.
	 */
	'aria-label'?: string
	className?: string
}

const trackSize = {
	md: 'h-1.5',
	sm: 'h-1',
} as const

/**
 * Progress — a determinate progress bar on the Radix base
 * (`@radix-ui/react-progress`): `role="progressbar"` with `aria-valuenow` /
 * `aria-valuemin` / `aria-valuemax` wired for free. A pill track on `--gray-3`
 * with a `--brand` fill that eases its width on `--duration-slow` /
 * `--ease-in-out` (the width jumps under `prefers-reduced-motion`). 100% keeps
 * the brand fill — no success recolor.
 *
 * Determinate only (`value` 0–100, clamped): there is no indeterminate mode —
 * for unknown-duration waits reach for Spinner, and for route transitions the
 * route-pending bar. `size` picks the track thickness (`md` 6px / `sm` 4px), and
 * an optional `label` renders a row above the track with the mono percentage.
 * Tokens only.
 */
function Progress({
	value,
	size = 'md',
	label,
	'aria-label': ariaLabel,
	className,
}: ProgressProps) {
	const clamped = Number.isFinite(value)
		? Math.min(100, Math.max(0, value))
		: 0
	const labelId = useId()

	return (
		<div data-slot="progress" className={cn('flex flex-col gap-1.5', className)}>
			{label != null ? (
				<div className="text-muted-foreground text-body-2xs flex items-center justify-between">
					<span id={labelId}>{label}</span>
					<span className="font-mono tabular-nums">{Math.round(clamped)}%</span>
				</div>
			) : null}
			<ProgressPrimitive.Root
				value={clamped}
				aria-labelledby={label != null ? labelId : undefined}
				aria-label={label == null ? ariaLabel : undefined}
				className={cn(
					'relative w-full overflow-hidden rounded-full bg-[var(--gray-3)]',
					trackSize[size],
				)}
			>
				<ProgressPrimitive.Indicator
					className="bg-brand h-full w-full rounded-full transition-transform duration-[var(--duration-slow)] ease-[var(--ease-in-out)] motion-reduce:transition-none"
					style={{ transform: `translateX(-${100 - clamped}%)` }}
				/>
			</ProgressPrimitive.Root>
		</div>
	)
}

export { Progress }
